import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Html5Qrcode as Html5QrcodeType } from 'html5-qrcode';
import { fulfillmentsApi } from '../../lib/api';
import Navbar from '../Navbar';

const READER_ELEMENT_ID = 'qr-reader';

export default function QRCodeScanner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const scannerRef = useRef<Html5QrcodeType | null>(null);
  // Guards against the camera success callback firing again (it keeps scanning
  // every frame) while a previous decode is already being verified/stopped.
  const handledRef = useRef(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    handledRef.current = false;
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(READER_ELEMENT_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleDecoded(decodedText),
        () => { /* per-frame "no code found yet" — not an error, ignore */ }
      );
      setScanning(true);
    } catch (err: any) {
      setError('Could not access camera. Please ensure camera permissions are granted.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (scanner && scanner.isScanning) {
      scanner.stop().then(() => scanner.clear()).catch(() => { /* already stopped/torn down */ });
    }
  };

  const handleDecoded = (decodedText: string) => {
    if (handledRef.current) return;
    handledRef.current = true;
    stopCamera();
    setScannedCode(decodedText.toUpperCase());
    verifyCode(decodedText.toUpperCase());
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scannedCode) return;
    await verifyCode(scannedCode);
  };

  const verifyCode = async (code: string) => {
    setVerifying(true);
    setError(null);

    try {
      const response = await fulfillmentsApi.verifyQR(id!, code);
      if (response.data.success) {
        setVerified(true);
        // Navigate to inspection page after short delay
        setTimeout(() => {
          navigate(`/fulfillment/${id}/inspect`, { state: { qrData: response.data.data } });
        }, 1500);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid QR code');
      // Let the renter try scanning again after a bad/expired code instead of
      // being stuck on the manual-entry-only view for the rest of the session.
      handledRef.current = false;
      if (!scanning) startCamera();
    } finally {
      setVerifying(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-xl border border-[var(--border)] p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="mdi mdi-check text-4xl text-green-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">QR Code Verified!</h2>
            <p className="text-[var(--text-secondary)]">Redirecting to equipment inspection...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <div className="container mx-auto px-4 max-w-md py-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <i className="mdi mdi-arrow-left"></i>
            Back
          </button>
          <h1 className="text-xl font-bold text-[var(--text-primary)] mt-2">Scan QR Code</h1>
        </div>

        {/* Camera View — Html5Qrcode renders its own <video> into this element. */}
        <div className="bg-black rounded-xl overflow-hidden mb-4 relative aspect-[3/4]">
          <div id={READER_ELEMENT_ID} className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover" />

          {/* Scan overlay */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-black/30"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64">
              <div className="relative w-full h-full">
                {/* Corner markers */}
                <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-[var(--forest-green)]"></div>
                <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-[var(--forest-green)]"></div>
                <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-[var(--forest-green)]"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-[var(--forest-green)]"></div>
              </div>
            </div>
          </div>

          {!scanning && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <button
                onClick={startCamera}
                className="btn btn-primary"
              >
                <i className="mdi mdi-camera mr-2"></i>
                Enable Camera
              </button>
            </div>
          )}
        </div>

        {/* Manual Entry */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-6">
          <p className="text-sm text-[var(--text-secondary)] text-center mb-4">
            Camera not working? Enter the code manually:
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleManualSubmit}>
            <input
              type="text"
              value={scannedCode || ''}
              onChange={(e) => setScannedCode(e.target.value.toUpperCase())}
              placeholder="Enter QR code (e.g., TR-ABC123)"
              className="w-full px-4 py-3 border border-[var(--border)] rounded-lg mb-4 font-mono text-center tracking-wider uppercase"
              disabled={verifying}
            />
            <button
              type="submit"
              disabled={!scannedCode || verifying}
              className="btn btn-primary w-full"
            >
              {verifying ? (
                <>
                  <i className="mdi mdi-loading mdi-spin mr-2"></i>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="mdi mdi-check mr-2"></i>
                  Verify Code
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
